import { Injectable, computed, inject, signal } from '@angular/core';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DateFormatService } from '@app/shared/services/shared/date-format.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { TimeReport, TimeReportStatus } from '@app/shared/types/models/reports/time-report';
import {
  SelectedTimeReportTypeEnum,
  TimeReportFilterConfig,
  TimeReportFilterName,
  TimeReportFilters,
  TimeReportKindFilter,
  TimeReportStatusFilter
} from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { isWorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { proffeoReportStatusToken } from '@app/shared/ui/tokens/report-status.tokens';
import { collectRowDateKeys, toTypeFilterOption } from '@app/shared/utils/time-report-table.utils';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import moment from 'moment';

/**
 * TimeReportPanelFiltersService - Builds Reports filter options and applied filter state.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: {@link WorkTimeReportComponent} and Reports filter UI.
 */
@Injectable({ providedIn: 'root' })
export class TimeReportPanelFiltersService {
  private readonly dateFormatService = inject(DateFormatService);
  private readonly translateService = inject(TranslateService);
  private tasksService = inject(TasksDataService);
  private projectsService = inject(ProjectsDataService);
  private locationsService = inject(LocationsDataService);
  private reportsBySelectedUsers = computed(() => {
    const selectedUsers = this.filtersState().users ?? [];
    const allReports = this.reports();
    return selectedUsers.length
      ? allReports.filter(report => {
          const user = this.getUserFromReport(report);
          return user && selectedUsers.includes(user.id);
        })
      : allReports;
  });

  public reports = signal<TimeReport[]>([]);
  public tableRows = signal<TimeReportRowVM[]>([]);
  public timeMode = signal<SelectedTimeReportTypeEnum>(SelectedTimeReportTypeEnum.WEEK);
  public startDate = signal<Date>(new Date());
  public endDate = signal<Date>(new Date());
  public filtersState = signal<TimeReportFilters>(this.getEmptyFilters());
  public appliedFilters = signal<TimeReportFilters>(this.getEmptyFilters());

  public availableDates = computed(() => {
    const start = moment(this.startDate()).startOf('day');
    const end = this.getEffectiveEndDate();
    const uniqueDates = new Set(collectRowDateKeys(this.tableRows()));

    return this.generateDateRange(start, end)
      .filter(date => uniqueDates.has(date))
      .map(date => this.createDateDropdownItem(date));
  });

  public availableUsers = computed(() =>
    this.createDropdownItems(
      this.reports(),
      report => this.getUserFromReport(report),
      user => user.id,
      user => user.fullName
    )
  );

  public availableCompanies = computed(() =>
    this.createDropdownItems(
      this.tableRows(),
      row => (row.companyId ? { id: row.companyId, name: row.companyName || row.companyId } : undefined),
      company => company.id,
      company => company.name
    )
  );

  public availableTasks = computed(() =>
    this.createDropdownItems(
      this.reportsBySelectedUsers().filter(isWorkTimeReport),
      report => report.taskId,
      taskId => taskId as string,
      taskId => {
        const task = this.tasksService.getById(taskId as string)();
        const taskType = task?.taskType?.();
        return `${taskType?.code || ''}-${task?.taskNumber || ''} ${task?.name || ''}`.trim();
      }
    )
  );

  public availableProjects = computed(() =>
    this.createDropdownItems(
      this.reportsBySelectedUsers().filter(isWorkTimeReport),
      report => report.projectId,
      projectId => projectId,
      projectId => this.projectsService.getById(projectId)()?.name || ''
    )
  );

  public availableLocations = computed(() =>
    this.createDropdownItems(
      this.reports().filter(isWorkTimeReport),
      report => report.locationId,
      locationId => locationId,
      locationId => this.locationsService.getById(locationId)()?.name || ''
    )
  );

  public availableTypes = computed(() => {
    return this.createDropdownItems(
      this.tableRows(),
      row => toTypeFilterOption(row),
      type => type.key,
      type => this.translateService.instant(type.labelKey)
    );
  });

  public availableKinds = computed<DropdownItem<string>[]>(() => {
    return [
      { value: 'All', label: this.translateService.instant('time-reports-filters.kind-all') },
      { value: 'WorkTime', label: this.translateService.instant('time-reports-filters.kind-work-time') },
      { value: 'Absence', label: this.translateService.instant('time-reports-filters.kind-absence') }
    ];
  });

  public availableStatuses = computed<DropdownItem<string>[]>(() => {
    return [
      { value: 'All', label: this.translateService.instant('time-reports-filters.status-all') },
      ...Object.values(TimeReportStatus).map(status => ({
        value: status,
        label: this.translateService.instant(proffeoReportStatusToken(status).filterLabelKey)
      }))
    ];
  });

  public filterConfigs = computed<TimeReportFilterConfig[]>(() => [
    {
      name: TimeReportFilterName.Kind,
      placeholder: 'time-reports-filters.filters-select-kind',
      items: this.availableKinds(),
      hideForAbsence: false,
      multiple: false
    },
    {
      name: TimeReportFilterName.Status,
      placeholder: 'time-reports-filters.filters-select-status',
      items: this.availableStatuses(),
      hideForAbsence: false,
      multiple: false
    },
    {
      name: TimeReportFilterName.Dates,
      placeholder: 'time-reports-filters.filters-select-date',
      items: this.availableDates(),
      hideForAbsence: false,
      multiple: true
    },
    {
      name: TimeReportFilterName.Users,
      placeholder: 'time-reports-filters.filters-select-user',
      items: this.availableUsers(),
      hideForAbsence: false,
      multiple: true
    },
    {
      name: TimeReportFilterName.Companies,
      placeholder: 'time-reports-filters.filters-select-company',
      items: this.availableCompanies(),
      hideForAbsence: false,
      multiple: true
    },
    {
      name: TimeReportFilterName.Types,
      placeholder: 'time-reports-filters.filters-select-type',
      items: this.availableTypes(),
      hideForAbsence: false,
      multiple: true
    },
    {
      name: TimeReportFilterName.Projects,
      placeholder: 'time-reports-filters.filters-select-project',
      items: this.availableProjects(),
      hideForAbsence: true,
      multiple: true
    },
    {
      name: TimeReportFilterName.Tasks,
      placeholder: 'time-reports-filters.filters-select-task',
      items: this.availableTasks(),
      hideForAbsence: true,
      multiple: true
    },
    {
      name: TimeReportFilterName.Locations,
      placeholder: 'time-reports-filters.filters-select-location',
      items: this.availableLocations(),
      hideForAbsence: true,
      multiple: true
    }
  ]);

  private getEmptyFilters(): TimeReportFilters {
    return {
      kind: 'All',
      status: 'All',
      dates: [],
      users: [],
      companies: [],
      tasks: [],
      projects: [],
      locations: [],
      types: []
    };
  }

  private getEffectiveEndDate(): moment.Moment {
    const timeMode = this.timeMode();
    const start = moment(this.startDate()).startOf('day');
    const end = moment(this.endDate()).startOf('day');
    const today = moment().startOf('day');

    return timeMode === SelectedTimeReportTypeEnum.DAY ? start : end.isAfter(today) ? today : end;
  }

  private generateDateRange(start: moment.Moment, end: moment.Moment): string[] {
    const dates: string[] = [];
    const current = end.clone();

    while (current.isSameOrAfter(start, 'day')) {
      dates.push(current.format('YYYY-MM-DD'));
      current.subtract(1, 'day');
    }

    return dates;
  }

  private createDateDropdownItem(dateStr: string): DropdownItem<string> {
    return {
      value: dateStr,
      label: this.dateFormatService.format(moment(dateStr).toDate()) || dateStr
    };
  }

  private createDropdownItems<TItem, TValue>(
    items: TItem[],
    getValue: (item: TItem) => TValue | undefined,
    getId: (value: TValue) => string,
    getLabel: (value: TValue) => string
  ): DropdownItem<string>[] {
    const seen = new Set<string>();
    const dropdownItems: DropdownItem<string>[] = [];

    items.forEach(item => {
      const value = getValue(item);
      if (!value) return;

      const id = getId(value);
      if (!seen.has(id)) {
        seen.add(id);
        dropdownItems.push({ value: id, label: getLabel(value) });
      }
    });

    return sortBy(dropdownItems, 'label');
  }

  private getUserFromReport(report: TimeReport): { id: string; fullName: string } | undefined {
    return report.user?.();
  }

  public getSelectedValues(filterName: TimeReportFilterName): string[] {
    if (filterName === TimeReportFilterName.Kind) {
      return [this.filtersState().kind ?? 'All'];
    }
    if (filterName === TimeReportFilterName.Status) {
      return [this.filtersState().status ?? 'All'];
    }

    const selected = this.filtersState()[filterName];
    return Array.isArray(selected) ? selected : [];
  }

  public updateFilter(filterName: TimeReportFilterName, value: string[]): void {
    if (filterName === TimeReportFilterName.Kind) {
      const kind = (value[0] as TimeReportKindFilter | undefined) ?? 'All';
      this.filtersState.update(filters => ({ ...filters, kind }));
      return;
    }
    if (filterName === TimeReportFilterName.Status) {
      const status = (value[0] as TimeReportStatusFilter | undefined) ?? 'All';
      this.filtersState.update(filters => ({ ...filters, status }));
      return;
    }

    this.filtersState.update(filters => ({ ...filters, [filterName]: value || [] }));
    if (filterName === TimeReportFilterName.Users) {
      const availableTaskIds = new Set(this.availableTasks().map(task => task.value));
      const availableProjectIds = new Set(this.availableProjects().map(project => project.value));
      this.filtersState.update(filters => ({
        ...filters,
        tasks: (filters.tasks ?? []).filter(id => availableTaskIds.has(id)),
        projects: (filters.projects ?? []).filter(id => availableProjectIds.has(id))
      }));
    }
  }

  public applyFilters(): void {
    this.appliedFilters.set({ ...this.filtersState() });
  }

  public resetFilters(): void {
    this.filtersState.set(this.getEmptyFilters());
    this.appliedFilters.set(this.getEmptyFilters());
  }
}
